import React, { Component } from 'react'
import { useSelector } from 'react-redux'
import { FormattedMessage } from 'react-intl'
import {
  AVMDiffClicked,
  AVMDiffLoad,
  NDStateWarnClicked,
  NDStateWarnLoad,
  LearnMore,
  ScrolledIntoView,
  EVENTS_LABELS
} from 'src/constants/trackingEvents'
import { ABBREVIATED_MONTH_WITH_YEAR } from 'src/constants/formats'
import moment from 'moment'
import { get } from 'lodash'
import { Waypoint } from 'react-waypoint'
import Panel from 'src/Components/Panel'
import { Avatar, BorderedTag, Button, If } from 'src/Components'
import { HeightFade } from 'src/Components/Transition'
import BotHeadColored from 'src/Components/Avatar/BotHeadColored'
import AdditionalInfo from 'src/Components/AdditionalInfo'
import { IconDollar, IconHome } from 'src/Components/Icon'
import { formatCurrency } from 'src/helpers/formatters'
import Chart from './Chart'
import AppreciationSentence from './AppreciationSentence'
import CMASection from './CMASection'
import NetWorthSection from './NetWorthSection'
import Text from 'src/Components/Text'
import {
  dispatchEvent,
  oneTimeTagEventDispatcher
} from 'src/helpers/tagManager'
import EstimatedValueZeroState from './EstimatedValueZeroState'
import GemContainer from 'src/Components/Gem/GemContainer'
import RefiOptionCard from 'src/Components/RefiOption/RefiOptionCard'
import GemChip from 'src/Components/Gem/GemChip'
import InstantOffer from 'src/Components/EstimatedValue/InstantOffer'
import {
  selectCustomerIsREA,
  selectCustomerPhotoUri
} from 'src/store/selectors/customerProfile'
import Blur from 'src/Components/Blur'

import styles from './EstimatedValue.module.scss'

const warningModalLink = (
  <FormattedMessage
    id='Homeowner.estimatedValue.avmAndDisclosureLearnMore'
    defaultMessage='Learn more about this.'
  />
)

const infoToggleId = 'AdditionalInfo_HF'

const scrollIntoView = oneTimeTagEventDispatcher(ScrolledIntoView, {
  eventLabel: EVENTS_LABELS.EstimatedValueModule
})

const VerifyChip = props => {
  const showAvatar = useSelector(selectCustomerIsREA)
  const src = useSelector(selectCustomerPhotoUri)

  return (
    <>
      <If condition={showAvatar}>
        <Avatar src={src} {...props} />
      </If>
      <If condition={!showAvatar}>
        <GemChip {...props}>
          <IconHome />
        </GemChip>
      </If>
    </>
  )
}

class EstimatedValue extends Component {
  state = {
    showInstantOffer: false,
    showCMA: false,
    instantOfferRef: null,
    cmaStep: null,
    cmaRef: null
  }

  toggleCMA = (showCMA = !this.state.showCMA) => {
    if (!this.props.withCurrentHomeData) {
      return
    }

    this.props.redrawHeightFade(infoToggleId)

    this.setState({
      showCMA,
      cmaStep: null,
      showInstantOffer: showCMA ? false : undefined
    })
  }

  toggleInstantOffer = (showInstantOffer = !this.state.showInstantOffer) => {
    if (!this.props.withCurrentHomeData) {
      return
    }

    this.props.redrawHeightFade(infoToggleId)

    this.setState({
      showInstantOffer,
      showCMA: showInstantOffer ? false : undefined
    })
  }

  handleSetCmaStep = step => () => {
    this.props.redrawHeightFade(infoToggleId, 'fast')

    this.setState({ cmaStep: step })
  }

  setCmaRef = ref => {
    this.setState({ cmaRef: ref })
  }

  setInstantOfferRef = ref => {
    this.setState({ instantOfferRef: ref })
  }

  renderWarnings () {
    const {
      stateName,
      showAVMDiffWarning,
      showNonDisclosureStateWarning,
      toggleWarningModal,
      withCurrentHomeData
    } = this.props

    if (!withCurrentHomeData) {
      return null
    }

    let message

    if (showAVMDiffWarning) {
      dispatchEvent(AVMDiffLoad)
      message = (
        <FormattedMessage
          id='Homeowner.estimatedValue.avmDiffWarning'
          defaultMessage='Your value has changed significantly since last month, which sometimes indicates an accuracy issue.'
        />
      )
    }

    if (showNonDisclosureStateWarning) {
      dispatchEvent(NDStateWarnLoad)
      message = (
        <FormattedMessage
          id='Homeowner.estimatedValue.nonDisclosureStateWarning'
          defaultMessage='{state} limits the amount of information Homebot can access and this can create an accuracy issue.'
          values={{
            state: stateName
          }}
        />
      )
    }

    if (!message) {
      return
    }

    return (
      <div className={styles.callout}>
        <div>
          <BotHeadColored size='xs' quizzical type='error' />
        </div>
        <div className={styles.calloutMessage}>
          {message}{' '}
          <Button
            minimal
            onClick={toggleWarningModal}
            track={showAVMDiffWarning ? AVMDiffClicked : NDStateWarnClicked}
          >
            {warningModalLink}
          </Button>
        </div>
      </div>
    )
  }

  render () {
    const {
      avm,
      customerProfile,
      hasCMA,
      home,
      withCurrentHomeData,
      instantOfferEnabled,
      disableValue
    } = this.props
    const { showInstantOffer, showCMA } = this.state
    const cmaDate = get(home, 'cma.updated')

    if (!avm) {
      return <EstimatedValueZeroState customerProfile={customerProfile} />
    }

    return (
      <Panel data-qa='EstimatedValueCard' className={styles.container}>
        <Panel.Heading className={styles.heading}>
          <FormattedMessage
            id='Homeowner.estimatedValue.headline'
            defaultMessage='The estimated value of your home is'
          />
        </Panel.Heading>
        <section className={styles.section}>
          <div className={styles.numberRow}>
            <div className={styles.avm}>
              <Button
                minimal
                className={styles.avmTotalButton}
                onClick={() => this.toggleCMA()}
                track={LearnMore}
                trackEnabled={!showCMA}
                trackOptions={{ eventLabel: 'EstValue-OpenCMA' }}
              >
                <Blur amount='most' enabled={!!disableValue}>
                  {formatCurrency(avm)}
                </Blur>
              </Button>

              <If condition={withCurrentHomeData}>
                <span className={styles.cmaEmblem}>
                  <If condition={!hasCMA}>
                    <BorderedTag type='danger'>
                      <FormattedMessage
                        id='Homeowner.estimatedValue.notVerifiedTag'
                        defaultMessage='Not verified yet'
                      />
                    </BorderedTag>
                  </If>
                </span>
              </If>
            </div>

            <If condition={hasCMA && withCurrentHomeData}>
              <div>
                <Text color='quiet' size='s'>
                  <FormattedMessage
                    id='Homeowner.estimatedValue.cmaVerifiedMessage'
                    defaultMessage='Verified by {customerName} {monthYear}'
                    values={{
                      monthYear: moment(cmaDate).format(
                        ABBREVIATED_MONTH_WITH_YEAR
                      ),
                      customerName: customerProfile.firstName
                    }}
                  />
                </Text>
              </div>
            </If>
          </div>

          {this.renderWarnings()}

          <Button.Group className={styles.contactButtons}>
            <If condition={!hasCMA && withCurrentHomeData}>
              <GemContainer
                data-qa='cmaCTA'
                onClick={() => this.toggleCMA()}
                track={LearnMore}
                trackEnabled={!showCMA}
                trackOptions={{ eventLabel: 'EstValue-OpenCMA' }}
                ref={this.handleSetCmaStep}
              >
                <RefiOptionCard className={styles.card}>
                  <VerifyChip className={styles.chip} />
                  <Text color='blue' size='s'>
                    <FormattedMessage
                      id='Homeowner.estimatedValue.verifyButton'
                      defaultMessage='Verify this value'
                    />
                  </Text>
                </RefiOptionCard>
              </GemContainer>
            </If>
            <If condition={hasCMA}>
              <GemContainer
                data-qa='cmaReverifyCTA'
                onClick={() => this.toggleCMA()}
                track={LearnMore}
                trackEnabled={!showCMA}
                trackOptions={{ eventLabel: 'EstValue-OpenReverifyCMA' }}
                ref={this.handleSetCmaStep}
              >
                <RefiOptionCard className={styles.card}>
                  <VerifyChip className={styles.chip} />
                  <Text color='blue' size='s'>
                    <FormattedMessage
                      id='Homeowner.estimatedValue.reverifyButton'
                      defaultMessage='Reverify this value'
                    />
                  </Text>
                </RefiOptionCard>
              </GemContainer>
            </If>
            <If condition={instantOfferEnabled}>
              <GemContainer
                data-qa='instantOfferCTA'
                onClick={() => this.toggleInstantOffer()}
                track={LearnMore}
                trackEnabled={!showInstantOffer}
                trackOptions={{ eventLabel: 'EstValue-OpenInstantOffer' }}
                ref={this.setInstantOfferRef}
              >
                <RefiOptionCard className={styles.card}>
                  <GemChip className={styles.chip}>
                    <Text color='green' element='span'>
                      <IconDollar />
                    </Text>
                  </GemChip>
                  <Text color='blue' size='s'>
                    <FormattedMessage
                      id='AVM.primarybutton.ibuyer.getcashoffer'
                      defaultMessage='Get cash offer'
                    />
                  </Text>
                </RefiOptionCard>
              </GemContainer>
            </If>
          </Button.Group>

          <If condition={withCurrentHomeData}>
            <AdditionalInfo
              visible={showCMA || showInstantOffer}
              cardWidth
              className={styles.additionalInfo}
              showDisclaimers={false}
              targetRef={
                this.state.showCMA
                  ? this.state.cmaRef
                  : this.state.instantOfferRef
              }
            >
              <HeightFade
                id={infoToggleId}
                speed={this.state.cmaStep && showCMA ? 'fast' : 'medium'}
              >
                <If condition={showCMA}>
                  <CMASection
                    onStepChange={this.handleSetCmaStep}
                    close={() => this.toggleCMA()}
                  />
                </If>
                <If condition={showInstantOffer}>
                  <InstantOffer />
                </If>
                <If condition={!showCMA && !showInstantOffer}>
                  <span />
                </If>
              </HeightFade>
            </AdditionalInfo>
          </If>

          <Chart disableValue={!!disableValue} />
          <AppreciationSentence className={styles.appreciationText} />
        </section>

        <NetWorthSection locked={this.props.locked} />
        <Waypoint onEnter={scrollIntoView} bottomOffset='25%'>
          <div />
        </Waypoint>
      </Panel>
    )
  }
}

export default EstimatedValue
